import { useRef, useEffect } from 'react';
import { ESRI } from '@rsos/maps/constants/mapProviders';
import { MARKER_TYPE_TEXT } from '@rsos/maps/constants/markerTypes';
import { createCircle } from '@rsos/maps/interface/circles';
import { createMarker } from '@rsos/maps/interface/markers';
import { panMapTo } from '@rsos/utils/panMapToCoordinate';

const ERAColor = [10, 124, 92]; // #0A7C5B
const ERAZoomLevel = 19;

const useDrawERA = (map, data) => {
  const markerRef = useRef();
  const circleRef = useRef();

  const handleZoomChange = newZoomLevel => {
    switch (map?.mapProvider) {
      case ESRI: {
        if (newZoomLevel < 19 && markerRef.current) {
          map.mapInstance.view.graphics.remove(markerRef.current);
        } else if (markerRef.current) {
          map.mapInstance.view.graphics.add(markerRef.current);
        }
        break;
      }
      default:
        return null;
    }
  };

  useEffect(() => {
    switch (map?.mapProvider) {
      case ESRI: {
        const zoomWatcher = map.mapInstance.view.watch(
          'zoom',
          handleZoomChange,
        );
        return () => {
          try {
            zoomWatcher.remove();
          } catch {
            /* suppress */
          }
        };
      }
      default:
        return () => {};
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);

  useEffect(() => {
    if (data) {
      const { data: era } = data;
      const { location } = era;
      const { hunc, latitude, longitude } = location;

      const latlng = {
        lat: parseFloat(latitude),
        lng: parseFloat(longitude),
      };
      switch (map?.mapProvider) {
        case ESRI: {
          if (markerRef.current) {
            map.mapInstance.view.graphics.remove(markerRef.current);
          }
          if (circleRef.current) {
            map.mapInstance.view.graphics.remove(circleRef.current);
          }
          markerRef.current = null;
          circleRef.current = null;
          createMarker({
            mapObject: map,
            options: {
              point: latlng,
              custom: {
                id: 'era-marker',
              },
              color: ERAColor,
              haloColor: 'black',
              haloSize: '1px',
              text: 'Emergency Response Area',
            },
            markerType: MARKER_TYPE_TEXT,
          }).then(marker => {
            markerRef.current = marker.instance;
            map.mapInstance.view.graphics.add(marker.instance);
          });

          createCircle({
            mapObject: map,
            circleOptions: {
              center: [latlng.lng, latlng.lat],
              custom: {
                id: 'era-circle',
              },
              radius: hunc,
            },
            graphicOptions: {
              symbol: {
                type: 'simple-fill',
                style: 'solid',
                color: [...ERAColor, 0.15],
                outline: {
                  width: 2,
                  color: ERAColor,
                },
              },
              visible: true,
            },
          }).then(circle => {
            circleRef.current = circle.instance;
            map.mapInstance.view.graphics.add(circle.instance);
          });
          panMapTo({
            mapProvider: ESRI,
            mapInstance: map.mapInstance,
            latitude: latlng.latitude,
            longitude: latlng.longitude,
            zoom: ERAZoomLevel,
          });
          break;
        }
        default:
          break;
      }
    } else {
      // remove the marker and circle if it has been drawn
      if (markerRef.current && circleRef.current) {
        switch (map?.mapProvider) {
          case ESRI: {
            map.mapInstance.view.graphics.remove(markerRef.current);
            map.mapInstance.view.graphics.remove(circleRef.current);
            break;
          }
          default:
            break;
          // return null;
        }
        markerRef.current = null;
        circleRef.current = null;
      }
    }

    return () => {
      if (markerRef.current) {
        map.mapInstance.view.graphics.remove(markerRef.current);
      }
      if (circleRef.current) {
        map.mapInstance.view.graphics.remove(circleRef.current);
      }
      markerRef.current = null;
      circleRef.current = null;
    };
  }, [data, map]);

  return null;
};

export default useDrawERA;
